import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
    Card, Grid, Icon, IconButton, InputLabel, Tooltip, Box, 
    Avatar, MenuItem, Select, FormControl, Divider, Badge, Checkbox, ListItemText,
    Dialog, DialogTitle, DialogContent, CircularProgress, Paper
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { get } from "utils/WebService";
import routes from "routes";
import moment from "moment";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import { ESCURO } from "assets/coresTema";

export default function MonitoriaSystem({ standalone = false }) {
    const { userId, userFoto, userEmail, userFunctionalities, userPerfils, setUserPerfils } = useUser();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [equipes, setEquipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEquipes, setSelectedEquipes] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(30); // seconds
    const [currentTime, setCurrentTime] = useState(new Date());

    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [operatorResumoData, setOperatorResumoData] = useState(null);
    const [loadingOperatorResumo, setLoadingOperatorResumo] = useState(false);

    // Ref to hold current state to prevent stale closure in setInterval
    const refreshTimer = useRef(null);

    // Standalone profile loader
    useEffect(() => {
        if (!userPerfils || userPerfils.length === 0) {
            get('/users/by-token')
                .then((userData) => {
                    if (userData && userData.id) {
                        get(`/users/by-id/${userData.id}`)
                            .then((fullUser) => {
                                if (fullUser && fullUser.perfils) {
                                    setUserPerfils(fullUser.perfils);
                                }
                            })
                            .catch(err => console.error("Erro ao carregar perfil no standalone", err));
                    }
                })
                .catch(err => console.error("Erro ao carregar token no standalone", err));
        }
    }, [userPerfils, setUserPerfils]);

    const isLiberado = userPerfils?.some(p => {
        const n = (p.nome || p.ccNome || '').toLowerCase();
        return n.includes("gestor") || n.includes("admin") || n.includes("coord");
    });

    const isAdminOrGestor = useMemo(() => {
        return userPerfils?.some(p => {
            const n = (p.nome || p.ccNome || '').toLowerCase();
            return n.includes("gestor") || n.includes("admin");
        });
    }, [userPerfils]);

    const OPTION_ALL = useMemo(() => ({ id: "all", nome: "Todas as Equipes" }), []);

    const equipeOptions = useMemo(() => {
        if (isAdminOrGestor) {
            return [OPTION_ALL, ...equipes];
        }
        if (equipes.length > 1) {
            return [OPTION_ALL, ...equipes];
        }
        return equipes;
    }, [equipes, isAdminOrGestor, OPTION_ALL]);

    const selectedEquipeValue = useMemo(() => {
        if (isAdminOrGestor) {
            if (selectedEquipes.includes("all") || selectedEquipes.length === 0) {
                return [OPTION_ALL];
            }
            return equipes.filter(e => selectedEquipes.includes(e.id));
        } else {
            if (selectedEquipes.length > 0 && selectedEquipes[0] !== "all") {
                const found = equipes.filter(e => selectedEquipes.includes(e.id));
                if (found.length > 0) {
                    return equipes.length > 1 && selectedEquipes.length > 1 ? found : found[0];
                }
            }
            if (equipes.length > 0) {
                return equipes.length > 1 ? equipes : equipes[0];
            }
            return OPTION_ALL;
        }
    }, [selectedEquipes, equipes, isAdminOrGestor, OPTION_ALL]);

    const fetchMonitoriaData = () => {
        setLoading(true);
        get("/users/coordenador/equipes")
            .then((res) => {
                if (Array.isArray(res)) {
                    setEquipes(res);
                    setSelectedEquipes(prev => {
                        if (prev.length > 0) return prev;
                        if (isAdminOrGestor) {
                            return ["all"];
                        } else {
                            if (res.length > 0) {
                                return res.map(eq => eq.id);
                            }
                            return ["all"];
                        }
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching monitoria data:", err);
                setLoading(false);
            });
    };

    // Auto-refresh timer
    useEffect(() => {
        fetchMonitoriaData();
        
        // Update current time every second for dynamic durations
        const secTimer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => {
            clearInterval(secTimer);
        };
    }, []);

    useEffect(() => {
        if (refreshTimer.current) {
            clearInterval(refreshTimer.current);
        }

        if (refreshInterval > 0) {
            refreshTimer.current = setInterval(() => {
                fetchMonitoriaData();
            }, refreshInterval * 1000);
        }

        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
        };
    }, [refreshInterval]);

    const handleOpenOperatorFilaModal = (operator) => {
        setSelectedOperator(operator);
        setOperatorModalOpen(true);
        setLoadingOperatorResumo(true);
        setOperatorResumoData(null);

        const todayStr = moment().format("YYYY-MM-DD");
        get(`/fila-trabalho/resumo-home?userId=${operator.id}&data=${todayStr}`)
            .then((res) => {
                if (res) {
                    setOperatorResumoData(res);
                }
            })
            .catch((err) => {
                console.error("Erro ao buscar resumo da fila do operador:", err);
            })
            .finally(() => {
                setLoadingOperatorResumo(false);
            });
    };

    const getStatusRows = () => {
        if (!operatorResumoData) return [];
        const naFilaStatus = operatorResumoData.naFila?.porStatus || {};
        const trabalhadosStatus = operatorResumoData.trabalhados?.porStatus || {};
        const aTrabalharStatus = operatorResumoData.aTrabalhar?.porStatus || {};

        const allStatuses = Array.from(new Set([
            ...Object.keys(naFilaStatus),
            ...Object.keys(trabalhadosStatus),
            ...Object.keys(aTrabalharStatus),
        ]));

        return allStatuses.map((st) => ({
            status: st,
            naFila: naFilaStatus[st] || 0,
            aTrabalhar: aTrabalharStatus[st] || 0,
            trabalhados: trabalhadosStatus[st] || 0,
        }));
    };

    // Format helper functions
    const getPauseIcon = (motivo) => {
        if (!motivo) return "pause_circle";
        const m = motivo.toLowerCase();
        if (m.includes("almoç") || m.includes("almoc") || m.includes("lanche") || m.includes("refeição") || m.includes("refeicao") || m.includes("comida")) {
            return "restaurant";
        }
        if (m.includes("banheir") || m.includes("sanitári") || m.includes("sanitari") || m.includes("wc") || m.includes("privada")) {
            return "wc";
        }
        if (m.includes("treina") || m.includes("estudo") || m.includes("curso") || m.includes("aula")) {
            return "school";
        }
        if (m.includes("feedba")) {
            return "record_voice_over";
        }
        if (m.includes("reuni") || m.includes("conversa") || m.includes("alinha")) {
            return "groups";
        }
        if (m.includes("café") || m.includes("cafe")) {
            return "local_cafe";
        }
        if (m.includes("particu") || m.includes("descan") || m.includes("fumo")) {
            return "coffee";
        }
        return "pause_circle";
    };

    const getActionIcon = (actionStr) => {
        if (!actionStr || actionStr === "Offline") return "do_not_disturb_on";
        const a = actionStr.toLowerCase();
        if (a.includes("visualiz") || a.includes("base") || a.includes("ficha")) return "visibility";
        if (a.includes("atend") || a.includes("ligac") || a.includes("ligaç") || a.includes("chamad")) return "phone_in_talk";
        if (a.includes("acordo") || a.includes("negoc")) return "handshake";
        return "play_circle";
    };

    // dateFim: o logout ou, sem sinal do sistema, o último sinal; sem os dois (online), agora
    const formatLoggedDuration = (dateStart, dateFim) => {
        if (!dateStart) return "-";
        const start = new Date(dateStart).getTime();
        const end = dateFim ? new Date(dateFim).getTime() : currentTime.getTime();
        const diffMs = end - start;
        if (diffMs < 0) return "0s";
        const secs = Math.floor(diffMs / 1000);
        const mins = Math.floor(secs / 60);
        const hrs = Math.floor(mins / 60);
        if (hrs > 0) return `${hrs}h ${mins % 60}m`;
        if (mins > 0) return `${mins}m ${secs % 60}s`;
        return `${secs}s`;
    };

    const formatTimeAgo = (dateLast) => {
        if (!dateLast) return "-";
        const diffMs = currentTime.getTime() - new Date(dateLast).getTime();
        if (diffMs < 0) return "agora";
        const secs = Math.floor(diffMs / 1000);
        if (secs < 60) return `${secs}s atrás`;
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins}m atrás`;
        return `${Math.floor(mins / 60)}h atrás`;
    };

    // "há 12 min", "há 2h 05min": desde quando a pessoa está offline
    const formatOfflineHa = (dateLast) => {
        if (!dateLast) return "";
        const mins = Math.max(0, Math.floor((currentTime.getTime() - new Date(dateLast).getTime()) / 60000));
        if (mins < 60) return `há ${mins} min`;
        return `há ${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}min`;
    };

    const horaCurta = (data) => (data ? new Date(data).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "-");

    // A tela do sistema avisa a cada 45 s que está aberta (SystemWrapper); 5 min sem aviso = sem sinal (como no backend)
    const SEM_SINAL_MS = 5 * 60 * 1000;

    const formatSeconds = (totalSeconds) => {
        if (!totalSeconds) return "0s";
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        if (hrs > 0) return `${hrs}h ${mins % 60}m`;
        if (mins > 0) return `${mins}m ${secs % 60}s`;
        return `${secs}s`;
    };

    // Calculate general stats
    const stats = useMemo(() => {
        let totalMembros = 0;
        let onlineMembros = 0;
        let emAtendimento = 0;

        // Pessoa em mais de uma equipe selecionada conta uma vez só
        const contados = new Set();
        equipes.forEach(eq => {
            const isSelected = selectedEquipes.includes("all") || selectedEquipes.includes(eq.id);
            if (isSelected) {
                eq.membros?.forEach(m => {
                    if (contados.has(m.id)) return;
                    contados.add(m.id);
                    totalMembros++;
                    if (m.online) {
                        onlineMembros++;
                    }
                    if (m.idDevedor) {
                        emAtendimento++;
                    }
                });
            }
        });

        return { totalMembros, onlineMembros, emAtendimento };
    }, [equipes, selectedEquipes]);

    // Handle opening of devedor record
    const handleOpenDevedor = (idDevedor) => {
        const baseDeDadosRoute = routes
            .flatMap(r => r.collapse ? r.collapse : r)
            .find(r => r.key === "baseDeDados");

        if (standalone) {
            // We are in standalone tab: copy session token briefly to localStorage and open main system
            const token = sessionStorage.getItem('user');
            const userMenus = sessionStorage.getItem('userMenus');
            
            localStorage.setItem('temp_share_user', token);
            localStorage.setItem('temp_share_menus', userMenus);
            localStorage.setItem('pendingDevedorId', idDevedor);
            localStorage.setItem('pendingDevedorOrigin', 'monitoria_standalone');
            window.open('/system?view=baseDeDados', '_blank');
        } else {
            // We are inside the main system tab: load devedor and switch tab
            sessionStorage.setItem('pendingDevedorId', idDevedor);
            sessionStorage.setItem('pendingDevedorOrigin', 'monitoria');
            window.dispatchEvent(new CustomEvent('pendingDevedorChange', { detail: { id: idDevedor, origin: 'monitoria' } }));
            // Dispatch page select to SystemWrapper (passed via props/clone)
            if (window.onPageSelectCustom) {
                window.onPageSelectCustom(baseDeDadosRoute);
            }
        }
    };

    const handlePopout = () => {
        const token = sessionStorage.getItem('user');
        const userMenus = sessionStorage.getItem('userMenus');
        
        localStorage.setItem('temp_share_user', token);
        localStorage.setItem('temp_share_menus', userMenus);
        window.open('/system?view=monitoria', '_blank');
    };

    const getInitials = (name) => {
        if (!name) return "";
        const parts = name.split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getRandomColor = (name) => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colors = [
            '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', 
            '#2196f3', '#009688', '#4caf50', '#ff9800', 
            '#ff5722', '#795548'
        ];
        return colors[Math.abs(hash) % colors.length];
    };

    // Prepare table data
    const tableColumns = [
        { Header: "Operador", accessor: "nome", width: "15%" },
        { Header: "Status", accessor: "status", width: "10%", align: "left" },
        { Header: "Ação Corrente", accessor: "ultimaAcao", width: "15%" },
        { Header: "Login / Logout", accessor: "loginLogout", width: "10%" },
        { Header: "T. Logado", accessor: "tempoLogado", width: "10%" },
        { Header: "T. Pausa", accessor: "tempoPausa", width: "10%", align: "center" },
        { Header: "Fichas (Fila / Trab)", accessor: "fichas", width: "10%", align: "center" },
        { Header: "Acordos", accessor: "acordos", width: "10%", align: "center" },
        { Header: "Ficha / Tempo", accessor: "fichaAberta", width: "8%", align: "center" }
    ];

    const tableRows = useMemo(() => {
        const rows = [];
        // Uma linha por pessoa: quem está em mais de uma equipe selecionada (ou também em "Outros") aparece uma vez
        const mostrados = new Set();
        equipes.forEach(eq => {
            const isSelected = selectedEquipes.includes("all") || selectedEquipes.includes(eq.id);
            if (isSelected && eq.membros) {
                eq.membros.forEach(m => {
                    if (mostrados.has(m.id)) return;
                    mostrados.add(m.id);
                    const pausaMarcada = Boolean(m.emPausa || (m.ultimaAcao && m.ultimaAcao.toLowerCase().includes("pausa")));
                    // Sinal do sistema: o backend já diz "online" (aviso há menos de 5 min, sem pausa e sem logout); em
                    // pausa ele não diz, então olha o último aviso aqui
                    const ultimoSinal = m.dataUltimaAtividade || m.dataLogin;
                    const sinalRecente = Boolean(m.dataUltimaAtividade)
                        && currentTime.getTime() - new Date(m.dataUltimaAtividade).getTime() < SEM_SINAL_MS;
                    const ativo = !m.dataLogout && (m.online || (pausaMarcada && sinalRecente));
                    const isPause = pausaMarcada && ativo;
                    // Entrou hoje, não saiu pelo Sair e o sistema parou de avisar (navegador fechado, computador em
                    // suspensão, sem internet, sessão vencida): o tempo logado e a pausa param no último sinal
                    const semSinal = Boolean(m.dataLogin) && !m.dataLogout && !ativo;
                    const fimTempo = m.dataLogout || (semSinal ? ultimoSinal : null);

                    let pauseMotivo = m.motivoPausa;
                    if (!pauseMotivo && m.ultimaAcao) {
                        if (m.ultimaAcao.includes("(") && m.ultimaAcao.includes(")")) {
                            const startIdx = m.ultimaAcao.indexOf("(");
                            const endIdx = m.ultimaAcao.lastIndexOf(")");
                            pauseMotivo = m.ultimaAcao.substring(startIdx + 1, endIdx);
                        } else if (m.ultimaAcao.toLowerCase().includes("pausa")) {
                            pauseMotivo = m.ultimaAcao.replace(/em pausa/gi, "").replace(/pausa/gi, "").replace(/[()]/g, "").trim() || "Pausa";
                        }
                    }
                    if (!pauseMotivo) pauseMotivo = "Pausa";
                    const pauseIconName = getPauseIcon(pauseMotivo);

                    let statusNode;
                    if (ativo) {
                        statusNode = (
                            <MDBox display="flex" alignItems="center">
                                <span className="pulse-active" style={{ height: '10px', width: '10px', borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block', marginRight: '6px' }} />
                                <MDTypography variant="caption" color="success" fontWeight="bold">Online</MDTypography>
                            </MDBox>
                        );
                    } else {
                        const offlineDesde = m.dataLogout || (semSinal ? ultimoSinal : null);
                        statusNode = (
                            <Tooltip title={m.dataLogout ? `Saiu pelo sistema às ${horaCurta(m.dataLogout)}`
                                : semSinal ? `Sem sinal do sistema desde ${horaCurta(ultimoSinal)} (navegador fechado, computador em suspensão, sem internet ou sessão vencida)` : ""}>
                                <MDBox display="flex" alignItems="center">
                                    <span style={{ height: '10px', width: '10px', borderRadius: '50%', backgroundColor: '#9e9e9e', display: 'inline-block', marginRight: '6px' }} />
                                    <MDBox display="flex" flexDirection="column" lineHeight={1.2}>
                                        <MDTypography variant="caption" color="text" fontWeight="medium">Offline</MDTypography>
                                        {offlineDesde && (
                                            <MDTypography variant="caption" color="text" sx={{ fontSize: "10px", opacity: 0.85 }}>
                                                {formatOfflineHa(offlineDesde)}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </MDBox>
                            </Tooltip>
                        );
                    }

                    let actionNode;
                    if (isPause) {
                        actionNode = (
                            <Tooltip title={`Pausa: ${pauseMotivo}`}>
                                <MDBox display="flex" alignItems="center" gap={0.75}>
                                    <Icon color="warning" sx={{ fontSize: "18px !important" }}>{pauseIconName}</Icon>
                                    <MDTypography variant="caption" color="warning" fontWeight="bold" sx={{ maxWidth: "140px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {pauseMotivo}
                                    </MDTypography>
                                </MDBox>
                            </Tooltip>
                        );
                    } else if (ativo) {
                        const actText = m.ultimaAcao || "Sem atividade";
                        const actIcon = getActionIcon(actText);
                        actionNode = (
                            <Tooltip title={actText}>
                                <MDBox display="flex" alignItems="center" gap={0.75}>
                                    <Icon color="info" sx={{ fontSize: "18px !important" }}>{actIcon}</Icon>
                                    <MDTypography variant="caption" color="info" fontWeight="bold" sx={{ maxWidth: "140px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {actText}
                                    </MDTypography>
                                </MDBox>
                            </Tooltip>
                        );
                    } else {
                        actionNode = (
                            <MDBox display="flex" alignItems="center" gap={0.75}>
                                <Icon color="secondary" sx={{ fontSize: "18px !important", opacity: 0.6 }}>do_not_disturb_on</Icon>
                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                    Offline
                                </MDTypography>
                            </MDBox>
                        );
                    }

                    // Calculate pause time: active pause + accumulated daily total
                    // (sem sinal no meio de uma pausa: a pausa conta só até o último sinal)
                    let currentPauseSec = 0;
                    let totalAccumulatedSec = m.tempoTotalPausa || 0;
                    if (pausaMarcada && m.dataInicioPausa && !m.dataLogout) {
                        const start = new Date(m.dataInicioPausa).getTime();
                        const fim = isPause ? currentTime.getTime() : new Date(ultimoSinal).getTime();
                        currentPauseSec = Math.max(0, Math.floor((fim - start) / 1000));
                        totalAccumulatedSec += currentPauseSec;
                    }

                    let pauseNode;
                    if (isPause) {
                        pauseNode = (
                            <MDBox display="flex" flexDirection="column" alignItems="center" lineHeight={1.2}>
                                <Tooltip title={`Pausa Atual: ${pauseMotivo}`}>
                                    <MDTypography variant="caption" color="warning" fontWeight="bold">
                                        {formatSeconds(currentPauseSec)}
                                    </MDTypography>
                                </Tooltip>
                                <Tooltip title="Tempo Acumulado de Pausas no Dia">
                                    <MDTypography variant="caption" color="text" sx={{ fontSize: "10px", opacity: 0.85 }}>
                                        (Tot: {formatSeconds(totalAccumulatedSec)})
                                    </MDTypography>
                                </Tooltip>
                            </MDBox>
                        );
                    } else if (totalAccumulatedSec > 0) {
                        pauseNode = (
                            <Tooltip title="Tempo Acumulado de Pausas no Dia">
                                <MDTypography variant="caption" color="text" fontWeight="medium">
                                    {formatSeconds(totalAccumulatedSec)}
                                </MDTypography>
                            </Tooltip>
                        );
                    } else {
                        pauseNode = <MDTypography variant="caption" color="text">-</MDTypography>;
                    }

                    const memberPhoto = m.foto || (m.id === userId || (m.email && userEmail && m.email.toLowerCase() === userEmail.toLowerCase()) ? userFoto : null);

                    rows.push({
                        nome: (
                            <MDBox display="flex" alignItems="center">
                                {memberPhoto ? (
                                    <Avatar src={memberPhoto} sx={{ width: 32, height: 32, mr: 1.5, border: "1.5px solid rgba(255,255,255,0.8)", boxShadow: "0 2px 4px rgba(0,0,0,0.2)" }} />
                                ) : (
                                    <Avatar sx={{ bgcolor: getRandomColor(m.nome), width: 32, height: 32, fontSize: "0.85rem", fontWeight: "bold", mr: 1.5 }}>
                                        {getInitials(m.nome)}
                                    </Avatar>
                                )}
                                <MDBox display="flex" flexDirection="column">
                                    <MDTypography variant="caption" fontWeight="bold">{m.nome}</MDTypography>
                                    <MDTypography variant="caption" color="text" sx={{ fontSize: "10px" }}>{m.email}</MDTypography>
                                </MDBox>
                            </MDBox>
                        ),
                        status: statusNode,
                        ultimaAcao: actionNode,
                        loginLogout: (
                            <MDBox display="flex" flexDirection="column">
                                <MDTypography variant="caption" color="text">In: {m.dataLogin ? new Date(m.dataLogin).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "-"}</MDTypography>
                                {semSinal ? (
                                    <Tooltip title="Não saiu pelo Sair: horário do último sinal do sistema">
                                        <MDTypography variant="caption" color="text">
                                            Out: {new Date(ultimoSinal).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })} <span style={{ fontSize: "10px", opacity: 0.85 }}>(sem sinal)</span>
                                        </MDTypography>
                                    </Tooltip>
                                ) : (
                                    <MDTypography variant="caption" color="text">Out: {m.dataLogout ? new Date(m.dataLogout).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "-"}</MDTypography>
                                )}
                            </MDBox>
                        ),
                        tempoLogado: (
                            <Tooltip title={semSinal ? `Contado até o último sinal do sistema (${horaCurta(ultimoSinal)})` : ""}>
                                <MDTypography variant="caption" fontWeight="medium">
                                    {formatLoggedDuration(m.dataLogin, fimTempo)}
                                </MDTypography>
                            </Tooltip>
                        ),
                        tempoPausa: pauseNode,
                        fichas: (
                            <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.75}>
                                <MDTypography variant="caption" fontWeight="medium">
                                    <span style={{color: '#4caf50'}}>{m.fichasEmFila ?? m.fichasFinalizadas ?? 0}</span> / <span style={{color: '#2196f3'}}>{m.fichasTrabalhadas || 0}</span>
                                </MDTypography>
                                <Tooltip title="Ver resumo da fila por status">
                                    <IconButton size="small" color="info" onClick={() => handleOpenOperatorFilaModal(m)} sx={{ p: 0.25 }}>
                                        <Icon fontSize="small">bar_chart</Icon>
                                    </IconButton>
                                </Tooltip>
                            </MDBox>
                        ),
                        acordos: (
                            <Tooltip title={`À Vista: ${m.acordosAVista || 0} | Parcelados: ${m.acordosParcelados || 0}`}>
                                <MDBox display="flex" flexDirection="column" alignItems="center">
                                    <MDTypography variant="caption" fontWeight="bold" color="success">
                                        Total: {m.totalAcordos || 0}
                                    </MDTypography>
                                    <MDTypography variant="caption" fontSize="0.75rem" color="text" sx={{ opacity: 0.85 }}>
                                        AV: {m.acordosAVista || 0} | PRC: {m.acordosParcelados || 0}
                                    </MDTypography>
                                </MDBox>
                            </Tooltip>
                        ),
                        fichaAberta: (() => {
                            if (!m.idDevedor) {
                                return <MDTypography variant="caption" color="text">-</MDTypography>;
                            }

                            let tempoFichaSec = 0;
                            if (m.dataAberturaFicha) {
                                const startFicha = new Date(m.dataAberturaFicha).getTime();
                                tempoFichaSec = Math.max(0, Math.floor((currentTime.getTime() - startFicha) / 1000));
                            }

                            return (
                                <Tooltip title={`Devedor: ${m.nomeDevedor || "Em Atendimento"}${tempoFichaSec > 0 ? ` | Tempo na Ficha: ${formatSeconds(tempoFichaSec)}` : ""}`}>
                                    <MDBox
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        lineHeight={1.2}
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => handleOpenDevedor(m.idDevedor)}
                                    >
                                        {tempoFichaSec > 0 && (
                                            <MDTypography variant="caption" color="info" fontWeight="bold" sx={{ fontSize: "11px" }}>
                                                {formatSeconds(tempoFichaSec)}
                                            </MDTypography>
                                        )}
                                        <IconButton size="small" color="info" sx={{ p: 0.25 }}>
                                            <Icon fontSize="small">visibility</Icon>
                                        </IconButton>
                                    </MDBox>
                                </Tooltip>
                            );
                        })()
                    });
                });
            }
        });
        return rows;
    }, [equipes, selectedEquipes, currentTime]);

    return (
        <MDBox py={standalone ? 2 : 1}>
            <style>{`
                @keyframes pulse {
                    0% {
                        transform: scale(0.9);
                        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
                    }
                    70% {
                        transform: scale(1);
                        box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
                    }
                    100% {
                        transform: scale(0.9);
                        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
                    }
                }
                .pulse-active {
                    animation: pulse 2s infinite;
                }
            `}</style>

            {/* Header controls */}
            <Card variant="outlined" sx={{ p: 2, mb: 2, background: darkMode ? ESCURO.cartao : "#fafafa", borderColor: darkMode ? ESCURO.divisoria : "#ddd" }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={4} display="flex" alignItems="center">
                        <Icon color="info" fontSize="large" sx={{ mr: 1.5 }}>insights</Icon>
                        <MDBox ml={2} lineHeight={1}>
                            <MDTypography variant="h5" fontWeight="medium">
                                Painel de Monitoria
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    <Grid item xs={12} md={8} display="flex" flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={2}>
                        <MDBox width="250px">
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Filtrar Equipe</InputLabel>
                            <MDSelect
                                isMulti={isAdminOrGestor}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.nome}
                                name="filtro-equipes"
                                placeholder="Todas as Equipes"
                                value={selectedEquipeValue}
                                options={equipeOptions}
                                onChange={selectedOptions => {
                                    if (!selectedOptions) {
                                        setSelectedEquipes(["all"]);
                                        return;
                                    }
                                    if (isAdminOrGestor) {
                                        if (Array.isArray(selectedOptions)) {
                                            if (selectedOptions.length === 0) {
                                                setSelectedEquipes(["all"]);
                                            } else {
                                                const lastSelected = selectedOptions[selectedOptions.length - 1];
                                                if (lastSelected && lastSelected.id === "all") {
                                                    setSelectedEquipes(["all"]);
                                                } else {
                                                    const specific = selectedOptions.filter(o => o.id !== "all").map(o => o.id);
                                                    setSelectedEquipes(specific.length > 0 ? specific : ["all"]);
                                                }
                                            }
                                        }
                                    } else {
                                        setSelectedEquipes(selectedOptions.id ? [selectedOptions.id] : ["all"]);
                                    }
                                }}
                            />
                        </MDBox>

                        {/* Refresh Rate */}
                        <MDBox width="150px">
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Atualizar a cada</InputLabel>
                            <Select
                                value={refreshInterval}
                                onChange={(e) => setRefreshInterval(e.target.value)}
                                displayEmpty
                                fullWidth
                                variant="outlined"
                                size="small"
                               
                            >
                                <MenuItem value={0}>Manual</MenuItem>
                                <MenuItem value={15}>15s</MenuItem>
                                <MenuItem value={30}>30s</MenuItem>
                                <MenuItem value={60}>1 min</MenuItem>
                                <MenuItem value={120}>2 min</MenuItem>
                            </Select>
                        </MDBox>

                        <MDBox display="flex" alignItems="flex-end" pb={0.2} gap={1}>
                            <MDButton 
                                variant="gradient" 
                                color="info" 
                                size="medium"
                                onClick={fetchMonitoriaData}
                                disabled={loading}
                            >
                                <Icon>{loading ? "sync" : "refresh"}</Icon>
                                &nbsp;Atualizar
                            </MDButton>

                            {!standalone && (
                                <Tooltip title="Destacar em nova aba (Segundo Monitor)">
                                    <MDButton 
                                        variant="gradient" 
                                        color="dark" 
                                        size="medium"
                                        onClick={handlePopout}
                                    >
                                        <Icon>open_in_new</Icon>
                                        &nbsp;Destacar
                                    </MDButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    </Grid>
                </Grid>
            </Card>

            {/* Stats row */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? ESCURO.campo : "#ffffff", borderColor: darkMode ? ESCURO.borda : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="info" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">groups</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Total de Operadores</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold">{stats.totalMembros}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? ESCURO.campo : "#ffffff", borderColor: darkMode ? ESCURO.borda : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="success" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">online_prediction</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Ativos Agora</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" color="success">{stats.onlineMembros}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? ESCURO.campo : "#ffffff", borderColor: darkMode ? ESCURO.borda : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="warning" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">contact_phone</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Fichas em Atendimento</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" color="warning">{stats.emAtendimento}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>

            {/* Table layout instead of cards */}
            <Card variant="outlined" sx={{ background: darkMode ? ESCURO.campo : "#ffffff", borderColor: darkMode ? ESCURO.borda : "#eee" }}>
                <MDBox p={3} display="flex" alignItems="center">
                    <Icon color="info" sx={{ mr: 1 }}>view_list</Icon>
                    <MDTypography variant="h6" fontWeight="bold">
                        Lista de Operadores
                    </MDTypography>
                </MDBox>
                <Divider sx={{ m: 0 }} />
                <TableContainer>
                    <Table size="small">
                        <MDBox component="thead">
                            <TableRow>
                                {tableColumns.map((col, idx) => (
                                    <TableCell 
                                        key={idx} 
                                        align={col.align || "left"} 
                                        sx={{ width: col.width, color: darkMode ? "#fff" : "#333", fontWeight: "bold", fontSize: "12px", borderBottom: `1px solid ${darkMode ? ESCURO.divisoria : "#eee"}` }}
                                    >
                                        {col.Header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </MDBox>
                        <TableBody>
                            {tableRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 3, borderBottom: "none" }}>
                                        <MDTypography variant="button" color="text" fontStyle="italic">
                                            Nenhum operador encontrado para os filtros selecionados.
                                        </MDTypography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableRows.map((row, idx) => (
                                    <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                        {tableColumns.map((col, colIdx) => (
                                            <TableCell key={colIdx} align={col.align || "left"} sx={{ borderBottom: `1px solid ${darkMode ? ESCURO.divisoria : "#eee"}` }}>
                                                {row[col.accessor]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {/* Modal de Detalhamento da Fila do Operador por Status */}
            <Dialog
                open={operatorModalOpen}
                onClose={() => setOperatorModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        background: darkMode ? ESCURO.cartao : "#ffffff",
                        color: darkMode ? "#ffffff" : "#344767",
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                        <Icon color="info" sx={{ fontSize: 32 }}>
                            bar_chart
                        </Icon>
                        <MDBox>
                            <MDTypography variant="h5" fontWeight="bold" color="info">
                                Resumo da Fila: {selectedOperator?.nome || "Operador"}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                                Detalhamento dos casos alocados no dia agrupados por status da ficha
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <IconButton onClick={() => setOperatorModalOpen(false)} sx={{ color: darkMode ? "#fff" : "#333" }}>
                        <Icon>close</Icon>
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 3, borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
                    {loadingOperatorResumo ? (
                        <MDBox py={5} textAlign="center">
                            <CircularProgress color="info" size={36} />
                            <MDTypography variant="button" color="text" display="block" mt={1}>
                                Carregando resumo da fila do operador...
                            </MDTypography>
                        </MDBox>
                    ) : !operatorResumoData || (operatorResumoData.naFila?.total === 0 && operatorResumoData.trabalhados?.total === 0) ? (
                        <MDBox py={4} textAlign="center">
                            <MDTypography variant="body2" color="text">
                                Nenhum caso encontrado na fila deste operador no dia.
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox display="flex" flexDirection="column" gap={3}>
                            {/* 3 Blocos de Resumo */}
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ p: 2, background: darkMode ? ESCURO.painel : "#f8f9fa", border: "1px solid", borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
                                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                            <MDTypography variant="button" fontWeight="bold" color="info">
                                                1: Na Fila
                                            </MDTypography>
                                            <Icon color="info">queue</Icon>
                                        </MDBox>
                                        <MDTypography variant="h4" fontWeight="bold" color="text" mt={1}>
                                            {operatorResumoData.naFila?.total || 0} <span style={{ fontSize: "0.85rem", fontWeight: "normal" }}>casos</span>
                                        </MDTypography>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ p: 2, background: darkMode ? ESCURO.painel : "#f8f9fa", border: "1px solid", borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
                                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                            <MDTypography variant="button" fontWeight="bold" color="warning">
                                                2: A Trabalhar
                                            </MDTypography>
                                            <Icon color="warning">pending_actions</Icon>
                                        </MDBox>
                                        <MDTypography variant="h4" fontWeight="bold" color="text" mt={1}>
                                            {operatorResumoData.aTrabalhar?.total || 0} <span style={{ fontSize: "0.85rem", fontWeight: "normal" }}>casos</span>
                                        </MDTypography>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Card sx={{ p: 2, background: darkMode ? ESCURO.painel : "#f8f9fa", border: "1px solid", borderColor: darkMode ? ESCURO.divisoria : "#e0e0e0" }}>
                                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                            <MDTypography variant="button" fontWeight="bold" color="success">
                                                3: Trabalhados
                                            </MDTypography>
                                            <Icon color="success">task_alt</Icon>
                                        </MDBox>
                                        <MDTypography variant="h4" fontWeight="bold" color="text" mt={1}>
                                            {operatorResumoData.trabalhados?.total || 0} <span style={{ fontSize: "0.85rem", fontWeight: "normal" }}>casos</span>
                                        </MDTypography>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* Tabela de Detalhamento por Status */}
                            <TableContainer component={Paper} elevation={0} sx={{ background: "transparent", width: "100%", boxShadow: "none" }}>
                                <Table size="small" sx={{ width: "100%" }}>
                                    <MDBox component="thead">
                                        <TableRow>
                                            <DataTableHeadCell width="40%" sorted={false}>Status da Ficha</DataTableHeadCell>
                                            <DataTableHeadCell width="20%" align="center" sorted={false}>Na Fila (Total)</DataTableHeadCell>
                                            <DataTableHeadCell width="20%" align="center" sorted={false}>A Trabalhar</DataTableHeadCell>
                                            <DataTableHeadCell width="20%" align="center" sorted={false}>Trabalhados</DataTableHeadCell>
                                        </TableRow>
                                    </MDBox>
                                    <TableBody>
                                        {getStatusRows().map((row, idx) => (
                                            <TableRow key={idx} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                <TableCell width="40%" sx={{ py: 1.5, verticalAlign: 'top' }}>
                                                    <MDTypography variant="button" fontWeight="medium" color="text">
                                                        {row.status}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell width="20%" align="center" sx={{ py: 1.5, verticalAlign: 'top' }}>
                                                    <MDTypography variant="button" fontWeight="bold" color="info">
                                                        {row.naFila}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell width="20%" align="center" sx={{ py: 1.5, verticalAlign: 'top' }}>
                                                    <MDTypography variant="button" fontWeight="bold" color="warning">
                                                        {row.aTrabalhar}
                                                    </MDTypography>
                                                </TableCell>
                                                <TableCell width="20%" align="center" sx={{ py: 1.5, verticalAlign: 'top' }}>
                                                    <MDTypography variant="button" fontWeight="bold" color="success">
                                                        {row.trabalhados}
                                                    </MDTypography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </MDBox>
                    )}
                </DialogContent>
            </Dialog>
        </MDBox>
    );
}
